import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { InspectorsRepository } from "@/api/inspectors/repository";
import { InspectorsService } from "@/api/inspectors/service";
import { Form } from "@/components/controls";
import { AddressControl } from "@/components/controls/AddressControl";
import type { IAddress } from "@/types/address";
import type { IInspector } from "@/types/users-inspectors";
import { Button, Drawer, Toast } from "@/ui-kit";
import { AddressUtils } from "@/utils/address";
import { serverErrorHandler } from "@/utils/serverErrorHandler";
import { addressSchema } from "@/utils/validation";

type IProps = {
  inspector: IInspector;
  onSave: (updatedInspector: IInspector) => void;
  onDismiss: () => void;
};

type IInspectorDetailsFormData = {
  address: IAddress;
};

const EditModal = ({ inspector, onSave, onDismiss }: IProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);

  const schema = useMemo(() => {
    return z.object({
      address: addressSchema,
    });
  }, []);

  const form = useForm<IInspectorDetailsFormData>({
    defaultValues: {
      address: AddressUtils.getNormalized(inspector.address),
    },
    resolver: zodResolver(schema),
  });

  const { handleSubmit } = form;

  const repository = useMemo(() => {
    return new InspectorsRepository(new InspectorsService(), { abort: true });
  }, []);

  const onSubmit = useCallback(() => {
    handleSubmit((formData: IInspectorDetailsFormData) => {
      setLoading(true);

      repository
        .updateInspector({
          id: inspector.userId,
          userId: inspector.userId,
          address: formData.address,
        })
        .then((response) => {
          onSave(response);
          Toast.success({
            title: t("toast.addressUpdated"),
          });
          onDismiss();
        })
        .catch(
          serverErrorHandler(() => {
            setLoading(false);
          }),
        );
    })();
  }, [handleSubmit, repository, inspector.userId, onSave, onDismiss, t]);

  const handleDismiss = useCallback(() => {
    onDismiss();
  }, [onDismiss]);

  return (
    <FormProvider {...form}>
      <Drawer open={true} onOpenChange={onDismiss} closeOnOutsidePress={false}>
        <Drawer.Header title={t("label.address")} />
        <Drawer.Body>
          <Form>
            <AddressControl label={t("label.address")} name="address" />
          </Form>
        </Drawer.Body>
        <Drawer.Footer>
          <Button onClick={handleDismiss} variant="ghost" color="neutral">
            {t("action.cancel")}
          </Button>
          <Button onClick={onSubmit} loading={loading}>
            {t("action.save")}
          </Button>
        </Drawer.Footer>
      </Drawer>
    </FormProvider>
  );
};

export default EditModal;
